<template>
  <FinderCard data-test="finder-size-card" :title="criterion.title">
    <div class="flex flex-col">
      <fieldset
        :aria-label="criterion.title"
        class="flex w-full justify-between"
        data-test="finder-size-options"
      >
        <RevChip
          v-for="value in criterion.values"
          :key="value"
          class="body-1"
          :data-test="`finder-size-option-${Number.parseInt(value, 10)}`"
          :label="value"
          :model-value="isSelected(value)"
          size="medium"
          @click="toggleValue(value)"
        />
      </fieldset>

      <div class="flex flex-1 flex-col">
        <div
          class="flex flex-1 items-center justify-center"
          data-test="finder-size-illustration-wrapper"
        >
          <img
            alt=""
            aria-hidden="true"
            class="max-h-full max-w-full object-contain"
            data-test="finder-size-illustration"
            :src="macbookSizesImage"
          />
        </div>

        <div
          class="flex justify-between gap-16"
          data-test="finder-size-range-values"
        >
          <RevInputNumber
            id="finder-size-min-input"
            v-model="minModel"
            class="flex-1"
            :label="i18n(translations.rangeMinLabel)"
            :max="maxSize"
            :min="minSize"
            :show-clear-button="false"
            :show-stepper="false"
          />
          <RevInputNumber
            id="finder-size-max-input"
            v-model="maxModel"
            class="flex-1"
            :label="i18n(translations.rangeMaxLabel)"
            :max="maxSize"
            :min="minSize"
            :show-clear-button="false"
            :show-stepper="false"
          />
        </div>
      </div>
    </div>
  </FinderCard>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevChip } from '@ds/components/Chip'
import { RevInputNumber } from '@ds/components/InputNumber'

import type { FinderScreensizeCriterion } from '../../../../composables/useFinder/useFinder.types'
import { useFinderTracking } from '../../../../composables/useFinder/useFinderTracking'
import FinderCard from '../FinderCard/FinderCard.vue'

import translations from './FinderScreensizeTile.translations'
import macbookSizesImage from './macbook-sizes@2x.png'

const props = defineProps<{
  criterion: FinderScreensizeCriterion
}>()

const selectedValues = defineModel<string[]>({ default: () => [] })

const emit = defineEmits<{
  'select-range': [value: [number, number]]
}>()

const minSize = computed(() => props.criterion.rangeMin)
const maxSize = computed(() => props.criterion.rangeMax)

const i18n = useI18n()

const { trackFinderInteraction, trackFinderOptionClick } = useFinderTracking()

const selectedSizes = computed(() =>
  selectedValues.value
    .map((value) => Number.parseInt(value, 10))
    .filter((size) => Number.isFinite(size)),
)

const sizeRangeFromSelection = computed<[number, number]>(() => {
  if (selectedSizes.value.length === 0) return [minSize.value, maxSize.value]

  return [Math.min(...selectedSizes.value), Math.max(...selectedSizes.value)]
})

const minValue = ref<number>(sizeRangeFromSelection.value[0])
const maxValue = ref<number>(sizeRangeFromSelection.value[1])

let skipNextSelectionSync = false

watch(sizeRangeFromSelection, ([min, max]) => {
  if (skipNextSelectionSync) {
    skipNextSelectionSync = false

    return
  }

  minValue.value = min
  maxValue.value = max
})

function commitRangeChange(value: number | undefined, isMin: boolean) {
  if (value == null) return

  const clamped = Math.max(minSize.value, Math.min(value, maxSize.value))

  if (isMin) {
    minValue.value = clamped
    if (clamped > maxValue.value) maxValue.value = clamped
  } else {
    maxValue.value = clamped
    if (clamped < minValue.value) minValue.value = clamped
  }

  skipNextSelectionSync = true
  trackFinderInteraction({
    value: `${minValue.value}-${maxValue.value}`,
  })
  emit('select-range', [minValue.value, maxValue.value])
}

const minModel = computed<number>({
  get: () => minValue.value,
  set: (value) => commitRangeChange(value, true),
})

const maxModel = computed<number>({
  get: () => maxValue.value,
  set: (value) => commitRangeChange(value, false),
})

function isSelected(value: string): boolean {
  return selectedValues.value.includes(value)
}

function toggleValue(value: string): void {
  trackFinderOptionClick(
    props.criterion.values.map((v) => ({ id: v, label: v })),
    value,
  )

  const current = new Set(selectedValues.value)
  if (current.has(value)) current.delete(value)
  else current.add(value)

  selectedValues.value = [...current]
}
</script>
